/**
 * Settings → System → App Version
 *
 * Admin control panel for the in-app update prompt. Set the latest version,
 * minimum supported version, force-update flag, and release notes here.
 * The mobile app reads this on launch and shows a banner or blocking modal.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Smartphone, Save, RefreshCw, AlertTriangle, Info, Download } from 'lucide-react';
import { apiGet, apiPut, BACKEND_URL } from '../../lib/api';

const PLAY_STORE_DEFAULT = 'https://play.google.com/store/apps/details?id=com.elrindia.erp';

export default function AppVersion() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    latest_version: '1.0.20',
    latest_version_code: 20,
    min_supported_code: 1,
    force_update: false,
    release_notes: '',
    play_store_url: PLAY_STORE_DEFAULT,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    apiGet('/app/version-info').then((d) => {
      if (d) setForm({
        latest_version:      d.latest_version      || '1.0.20',
        latest_version_code: d.latest_version_code ?? 20,
        min_supported_code:  d.min_supported_code  ?? 1,
        force_update:        !!d.force_update,
        release_notes:       d.release_notes       || '',
        play_store_url:      d.play_store_url       || PLAY_STORE_DEFAULT,
      });
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const upd = (k) => (e) => setForm((f) => ({
    ...f,
    [k]: e.target.type === 'checkbox' ? e.target.checked
       : e.target.type === 'number'   ? parseInt(e.target.value || '0', 10)
       : e.target.value,
  }));

  const save = async () => {
    if (form.min_supported_code > form.latest_version_code) {
      toast.error('Min supported code cannot exceed latest version code');
      return;
    }
    setSaving(true);
    try {
      await apiPut('/app/version-info', form);
      toast.success('App version settings saved');
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20 text-slate-400">
      <RefreshCw size={18} className="animate-spin" />
    </div>
  );

  const willTriggerForce = form.force_update;
  const willBlockOld = form.min_supported_code > 1;

  return (
    <div className="max-w-2xl mx-auto px-4 py-8 space-y-6">
      {/* Header */}
      <div>
        <button onClick={() => navigate('/app/settings')}
          className="flex items-center gap-1.5 text-xs font-display font-bold uppercase tracking-wider text-slate-500 hover:text-slate-800 mb-4">
          <ArrowLeft size={14} /> Settings
        </button>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#1F7A6E]/10 flex items-center justify-center">
            <Smartphone size={20} className="text-[#1F7A6E]" />
          </div>
          <div>
            <h1 className="font-display font-bold text-xl text-slate-900">App Version</h1>
            <p className="text-sm text-slate-500 mt-0.5">
              Control in-app update prompts on the Android/Capacitor app.
            </p>
          </div>
        </div>
      </div>

      {/* Live effect preview */}
      {(willTriggerForce || willBlockOld) && (
        <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800">
          <AlertTriangle size={16} className="shrink-0 mt-0.5 text-amber-500" />
          <div>
            {willTriggerForce && <p><strong>Force update is ON</strong> — all users will see a blocking modal until they update, regardless of their version.</p>}
            {!willTriggerForce && willBlockOld && <p><strong>Min supported code {form.min_supported_code}</strong> — users on older builds will see a blocking modal.</p>}
          </div>
        </div>
      )}

      {/* Config card */}
      <div className="bg-white border border-slate-200 rounded-lg divide-y divide-slate-100">

        {/* Latest version */}
        <div className="px-5 py-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-semibold text-slate-600">Latest Version</span>
            <button
              onClick={() => setForm(f => ({ ...f, latest_version: '1.0.34', latest_version_code: 35,
                release_notes: 'Kiosk photo fix, Access Control, Mobile ESS/Approvals, 220+ link fixes, Head Office all-project access' }))}
              className="text-[10px] font-bold text-[#1F7A6E] border border-[#1F7A6E]/30 rounded px-2 py-0.5 hover:bg-[#1F7A6E]/5"
              data-testid="av-quick-fill-btn"
            >
              ← Set Latest (v1.0.34 · build 35)
            </button>
          </div>
          <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">
              Version Name
            </label>
            <input type="text" value={form.latest_version} onChange={upd('latest_version')}
              placeholder="1.0.34"
              className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#1F7A6E]"
              data-testid="av-latest-version" />
            <p className="text-xs text-slate-400 mt-1">Human-readable, e.g. 1.0.34</p>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">
              Version Code <span className="text-slate-400 font-normal">(integer)</span>
            </label>
            <input type="number" min={1} value={form.latest_version_code} onChange={upd('latest_version_code')}
              className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#1F7A6E]"
              data-testid="av-latest-code" />
            <p className="text-xs text-slate-400 mt-1">Matches <code className="bg-slate-100 px-1 rounded">versionCode</code> in build.gradle</p>
          </div>
          </div>
        </div>

        {/* Min supported */}
        <div className="px-5 py-4">
          <label className="block text-xs font-semibold text-slate-600 mb-1.5">
            Minimum Supported Version Code
          </label>
          <input type="number" min={1} value={form.min_supported_code} onChange={upd('min_supported_code')}
            className="w-48 border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#1F7A6E]"
            data-testid="av-min-code" />
          <p className="text-xs text-slate-400 mt-1">
            Users below this code see a <strong>blocking modal</strong> — they cannot use the app until they update.
            Keep at 1 to never block (soft updates only).
          </p>
        </div>

        {/* Force update */}
        <div className="flex items-center justify-between px-5 py-4">
          <div>
            <div className="font-semibold text-sm text-slate-800">Force update (override)</div>
            <div className="text-xs text-slate-500 mt-0.5">
              Shows blocking modal to <em>all</em> users regardless of version. Use only for critical security patches.
            </div>
          </div>
          <button
            onClick={() => setForm((f) => ({ ...f, force_update: !f.force_update }))}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              form.force_update ? 'bg-rose-500' : 'bg-slate-200'
            }`}
            data-testid="av-force-toggle"
          >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
              form.force_update ? 'translate-x-6' : 'translate-x-1'
            }`} />
          </button>
        </div>

        {/* Release notes */}
        <div className="px-5 py-4">
          <label className="block text-xs font-semibold text-slate-600 mb-1.5">
            Release Notes <span className="text-slate-400 font-normal">(shown in update banner / modal)</span>
          </label>
          <textarea value={form.release_notes} onChange={upd('release_notes')} rows={3}
            placeholder="e.g. Face-attendance speed improved · Offline fuel log · Bug fixes"
            className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#1F7A6E] resize-none"
            data-testid="av-release-notes" />
        </div>

        {/* Play Store URL */}
        <div className="px-5 py-4">
          <label className="block text-xs font-semibold text-slate-600 mb-1.5">
            Play Store URL
          </label>
          <input type="url" value={form.play_store_url} onChange={upd('play_store_url')}
            className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#1F7A6E]"
            data-testid="av-play-store-url" />
        </div>
      </div>

      {/* Download latest AAB */}
      <div className="bg-white border border-slate-200 rounded-lg px-5 py-4">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-3">
          <Download size={15} className="text-[#1F7A6E]" /> Download Latest Build
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <a
            href={`${BACKEND_URL}/api/app/download/aab`}
            download
            className="flex items-center justify-center gap-2 bg-[#1F7A6E] hover:bg-[#14504E] text-white px-4 py-2.5 rounded-lg text-sm font-display font-bold transition-colors"
            data-testid="av-download-aab-btn"
          >
            <Download size={14} /> Download AAB (Play Store)
          </a>
          <a
            href={`${BACKEND_URL}/api/app/download/apk`}
            download
            className="flex items-center justify-center gap-2 bg-slate-100 hover:bg-slate-200 text-slate-700 px-4 py-2.5 rounded-lg text-sm font-display font-bold transition-colors"
            data-testid="av-download-apk-btn"
          >
            <Download size={14} /> Download APK (Direct Install)
          </a>
        </div>
        <p className="text-[11px] text-slate-400 mt-2">
          AAB is for uploading to Google Play Console. APK is for direct device installation.
          Both require admin login to download.
        </p>
      </div>

      {/* Save */}
      <button onClick={save} disabled={saving}
        className="flex items-center gap-2 bg-[#1F7A6E] hover:bg-[#14504E] text-white px-6 py-2.5 rounded-lg text-sm font-display font-bold transition-colors disabled:opacity-50"
        data-testid="av-save-btn"
      >
        {saving ? <RefreshCw size={15} className="animate-spin" /> : <Save size={15} />}
        Save Version Settings
      </button>

      {/* How it works */}
      <div className="bg-slate-50 border border-slate-200 rounded-lg px-5 py-4 space-y-2">
        <div className="flex items-center gap-2 text-xs font-semibold text-slate-700">
          <Info size={14} /> How the update prompt works
        </div>
        <ul className="text-xs text-slate-500 space-y-1 list-disc list-inside">
          <li>The Android app reads its own <code className="bg-white px-1 rounded border border-slate-200">versionCode</code> from <code>build.gradle</code> on every launch.</li>
          <li>It calls <code className="bg-white px-1 rounded border border-slate-200">GET /app/version-info</code> and compares.</li>
          <li>If <strong>running &lt; latest</strong> → green dismissible banner at the bottom.</li>
          <li>If <strong>running &lt; min_supported</strong> OR <strong>force_update = on</strong> → blocking modal, cannot be dismissed.</li>
          <li>Dismiss is per-session (banner reappears on next app launch).</li>
        </ul>
      </div>
    </div>
  );
}
